import { Component, OnInit, Input, ElementRef, Output, EventEmitter } from '@angular/core';
import { Vehicle } from 'src/app/shared/model/vehicle';
import { DealerService, BidStatus, AlternativeBidRequest } from 'src/app/shared/services/dealer.service';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { GaEventsService } from 'src/app/shared/services/ga-events.service';
import { UserInfo } from '@app/store/session/session.store';
import { SessionService } from '@app/store/session/session.service';
import { MarketPlacePersonalizationUtils } from '@app-components/layouts/shopkeeper-layout/marketplace-personalization';
import { OpenService } from '@app-services/open.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmSendAlternativeBidComponent } from '@app-components/dialog/dialog-confirm-send-alternative-bid/dialog-confirm-send-alternative-bid.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'app-make-alternative-bid',
  templateUrl: './make-alternative-bid.component.html',
  styleUrls: ['./make-alternative-bid.component.scss']
})
export class MakeAlternativeBidComponent implements OnInit {
  @Input() vehicle: Vehicle;
  @Input() showAlternativeBidForm: boolean;
  @Output() backToStandardBid = new EventEmitter<void>();
  @Output() alternativeBidSent = new EventEmitter<void>();

  isMobile: boolean;

  loading = false;
  alternativeBid: AlternativeBidRequest;
  alternativeBidErrorMessage: string;
  alternativeBidSuccess: boolean;
  showLatePaymentLink = false;
  latePaymentQueryParams = { expandedStatuses: '["SOLD_NO_PAYMENT"]' };

  valueControl = new FormControl('alternativeBidForm');
  user: UserInfo;

  constructor(
    private dealerService: DealerService,
    private router: Router,
    private eventsService: GaEventsService,
    private elRef: ElementRef,
    private openService: OpenService,
    private readonly dialog: MatDialog,
    private sessionService: SessionService,
    private readonly toastr: ToastrService) { }

  ngOnInit() {
    this.isMobile = window.innerWidth < 768;
    this.sessionService.getUser().subscribe(u => this.user = u);
    this.alternativeBid = new AlternativeBidRequest();
    this.alternativeBid.value = 0;
    this.openService.getLayoutVars().subscribe(result => {
      if(result) {
        MarketPlacePersonalizationUtils.setCssVar(this.elRef, '--button-branded4', result.primaryColor );
      }
    });
  }

  onClickSend(form: any): void {
    if (this.loading) {
      return;
    }

    const proceed = () => {
      if (form?.form?.valid) {
        this.addAlternativeBid();
      } else {
        form?.form?.markAllAsTouched();
      }
    };

    if (!form?.form?.valid) {
      if (this.isMobile) {
        this.toastr.warning('Informe um valor válido para enviar a proposta.');
      }
      proceed();
      return;
    }

    if (this.isMobile) {
      proceed();
      return;
    }

    this.dialog.open(DialogConfirmSendAlternativeBidComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'dialog-confirm-send-alternative-bid',
      data: {
        vehicle: this.vehicle,
        alternativeBid: this.alternativeBid
      }
    })
      .afterClosed()
      .subscribe(result => {
        if (result && result.confirm) {
          proceed();
        }
      });
  }

  addAlternativeBid(): void {
    if (this.loading) {
      return;
    }

    if (!this.valueControl.valid) {
      this.valueControl.markAsTouched();
      return;
    }
    this.loading = true;
    this.dealerService.addAlternativeBid(this.alternativeBid, this.vehicle.id)
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.alternativeBid = new AlternativeBidRequest();
            this.alternativeBidSuccess = true;
            this.eventsService.fireEvent('Proposta personalizada', 'Envio', 'vehicleId: ' + this.vehicle.id);
            this.alternativeBidSent.emit();
            if (this.isMobile) {
              this.toastr.success('Proposta personalizada enviada com sucesso!');
            }
          } else {
            const statusMessage = BidStatus[result.status];
            this.alternativeBidErrorMessage = statusMessage;
            if (statusMessage === BidStatus.LATE_PAYMENT) {
              this.showLatePaymentLink = true;
            }
            if (this.isMobile) {
              const msg = statusMessage ?? 'Não foi possível enviar a proposta. Tente novamente.';
              this.toastr.error(msg);
            }
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          if (this.isMobile) {
            this.toastr.error('Não foi possível enviar a proposta. Tente novamente.');
          }
        }
      });
  }

  calculateCommissionValue(): number {
    if (!this.vehicle?.comissionValueConfig) {
      return 0;
    }

    const bidValue = this.alternativeBid?.value || 0;
    const platesQtd = 1 + (this.vehicle.additionalPlatesQtd || 0);
    
    switch (this.vehicle.comissionValueConfig.type) {
      case 'PERCENTAGE':
        return (bidValue * this.vehicle.comissionValueConfig.value) / 100;
      case 'REAL':
        return (this.vehicle.comissionValueConfig.value || 0) * platesQtd;
      case 'REAL_AND_PERCENTAGE':
        const percentageValue = (bidValue * this.vehicle.comissionValueConfig.value) / 100;
        const additionalFee = (this.vehicle.comissionValueConfig.additionalFee || 0) * platesQtd;
        return additionalFee + percentageValue;
      default:
        return 0;
    }
  }

  calculateTotalOperation(): number {
    const bidValue = this.alternativeBid?.value || 0;
    return bidValue + this.calculateCommissionValue();
  }

  triggerBackToStandard(): void {
    this.backToStandardBid.emit();
  }
}
